import React, { useEffect, useRef, useState } from "react";
import { IEventSummary, IScore, IShow } from "@eagerdog/interfaces";
import {
  bestJuniorPool,
  buildJuniorPayload,
  juniorParticipationClasses,
  juniorPlacementClasses,
  juniorScoresFor,
  readJuniorPlacements,
} from "@eagerdog/scoring-core";
import { AxiosError } from "axios";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";

import { AwardDropdown, PlacementGrid, SaveNote } from "./awardFields";
import { padPlacements, shortLevel } from "./dogLabel";
import { NestedSection } from "./NestedSection";
import { conformationOf } from "./storedResults";

import styles from "./UKCConformationResults.module.scss";

interface IProps {
  scores: IScore[];
  event: IEventSummary;
  show: IShow;
  onSaved: () => void;
}

type SaveStatus = "idle" | "saving" | "saved" | "error";

const scoresFor = juniorScoresFor;

const JuniorShowmanshipPanel: React.FC<IProps> = ({ scores, event, show, onSaved }) => {
  const saved = conformationOf(show)?.junior_showmanship;
  const classes = juniorPlacementClasses(scores);
  const participation = juniorParticipationClasses(scores);

  const readPlacements = () => readJuniorPlacements(saved, classes);

  const [placements, setPlacements] = useState<Record<string, string[]>>(readPlacements);
  const [bestJunior, setBestJunior] = useState(saved?.best_junior || "");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const formRef = useRef({ placements: readPlacements(), bestJunior: saved?.best_junior || "" });
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (saveTimerRef.current) return;
    const nextPlacements = readPlacements();
    const nextBest = saved?.best_junior || "";
    setPlacements(nextPlacements);
    setBestJunior(nextBest);
    formRef.current = { placements: nextPlacements, bestJunior: nextBest };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!scores.length) return null;

  const triggerAutoSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (savedStatusTimerRef.current) clearTimeout(savedStatusTimerRef.current);
    setSaveStatus("idle");
    saveTimerRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      const current = formRef.current;
      try {
        await apiService.saveConformationJuniorResults(
          event._id,
          show._id,
          buildJuniorPayload({
            classes,
            placementsByLevel: current.placements,
            bestJunior: current.bestJunior,
          })
        );
        setSaveStatus("saved");
        onSaved();
        savedStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (error) {
        setSaveStatus("error");
        toast.show({
          title: "Error",
          content: "Failed to save junior showmanship results.",
          type: "fail",
          errorDetails: error as AxiosError,
        });
      } finally {
        saveTimerRef.current = null;
      }
    }, 800);
  };

  const changePlacement = (level: string, index: number, id: string) => {
    setPlacements((prev) => {
      const next = { ...prev, [level]: [...(prev[level] || padPlacements())] };
      next[level][index] = id;
      formRef.current.placements = next;
      return next;
    });
    triggerAutoSave();
  };

  const changeBest = (id: string) => {
    setBestJunior(id);
    formRef.current.bestJunior = id;
    triggerAutoSave();
  };

  const bestPool = bestJuniorPool(scores, placements);

  return (
    <NestedSection title="Junior Showmanship" meta={`${scores.length} entries`}>
      {participation.length > 0 && (
        <p className={styles.participationNote}>
          Pee Wee and Sub-Junior have no placements.
        </p>
      )}
      {classes.map((level) => (
        <div className={styles.classSection} key={level}>
          <div className={styles.classSectionTitle}>{shortLevel(level)}</div>
          <PlacementGrid
            junior
            scores={scoresFor(scores, level)}
            value={placements[level] || padPlacements()}
            onChange={(index, id) => changePlacement(level, index, id)}
          />
        </div>
      ))}
      {bestPool.length > 0 && (
        <>
          <div className={styles.sectionDivider}>Best Junior Handler</div>
          <div className={styles.awardGrid}>
            <AwardDropdown
              junior
              label="Best Junior Handler"
              scores={bestPool}
              value={bestJunior}
              onChange={changeBest}
            />
          </div>
        </>
      )}
      <SaveNote status={saveStatus} />
    </NestedSection>
  );
};

export default JuniorShowmanshipPanel;
